import { FarmBoxCommodityDestribution } from 'models/FarmBoxCommodityDestribution/farmBoxCommodityDestribution.model';
import { FieldDetails } from 'models/Farmbox/field.model';
import { TransportationDetails } from 'models/Farmbox/transportation.model';
import { FarmboxRawMaterialsData } from 'models/FarmboxRawMaterials/farmBoxRawMaterials.model';
import FarmboxRawMaterialsService from 'services/Farmbox/RawMaterials/farmboxRawMaterials.service';
import FarmboxCommodityDistributionService from 'services/Farmbox/CommodityDistribution/commodityDistribution.service';
import FarmTransportationService from 'services/FarmTransportationService/farmTransportation.service';
import FarmFuelAndEnergyService from 'services/FarmFuelAndEnergyService/farmFuelAndEnergy.service';
import FarmFieldService from 'services/FarmFieldService/farmField.service';
import useRedirect from 'shared/utils/useRedirect';
import useFarmboxRedirections from 'shared/utils/useFarmboxRedirections';
import { resetOriginsToEmptyArray } from 'shared/utils/resetOriginsToEmptyArray';
import { useEffect } from 'react';
import { LandMetaData } from 'constants/FarmboxConstants/stage4.constants';
import { useSearchParams } from 'react-router-dom';
import { adePage, commodity } from 'constants/constants';


export const useSubmitHandlers = (
  commodityId?: string,
  locationId?: string,
  activeYear?: number
) => {
  const { redirectToCommodityDraft } = useFarmboxRedirections();
  const {
    redirectToRespectiveScopeTab
  } = useRedirect();

  const { updateRawMaterials, draftButtonLoading: rawMaterialDraftLoading } =
    FarmboxRawMaterialsService();
  const {
    updateCommodityDistribution,
    draftButtonLoading: commodityDistributionDraftLoading,
  } = FarmboxCommodityDistributionService();
  const {
    updateTransportationDataAsDraft,
    draftButtonLoading: transportationDraftLoading,
  } = FarmTransportationService();
  const {
    updateFuelAndEnergyDataAsDraft,
    draftButtonLoading: fuelAndEnergyDraftLoading,
  } = FarmFuelAndEnergyService();
  const {
    updateFieldDataAsDraft,
    draftButtonLoading: fieldDraftLoading,
    landUseMetaData,
    landManagementMetaData,
    fetchLandMetaData,
  } = FarmFieldService();

  const [searchParams] = useSearchParams();
  const queryParams = Object.fromEntries(searchParams.entries());
  const { isFrom } = queryParams;

  useEffect(() => {
    if (!commodityId) return;

    fetchLandMetaData(LandMetaData.LAND_USE_META);
    fetchLandMetaData(LandMetaData.LAND_MANAGEMENT_META);
  }, [commodityId]);

  const handleNavigationDraft = () => {
    if (isFrom === adePage) {
      redirectToRespectiveScopeTab(locationId, commodity, String(activeYear));
    } else {
      redirectToCommodityDraft();
    }
  };

  return {
    submitHandlers: {
      stage1: async (data: FarmboxRawMaterialsData) => {
        const isPackagingMaterials = !data?.packagings?.[0]?.activityLabel;
        data?.fertilizers?.forEach(resetOriginsToEmptyArray);
        data?.packagings?.forEach(resetOriginsToEmptyArray);
        const areAllArraysEmpty =
          !data?.fertilizers?.[0]?.activityLabel && !isPackagingMaterials;

        if (isPackagingMaterials) {
          data.packagings = [];
        }

        if (!areAllArraysEmpty) {
          const success = await updateRawMaterials(
            Number(commodityId),
            data,
            true
          );
          if (success) handleNavigationDraft();
        }
      },

      stage2: async (data: TransportationDetails) => {
        const success = await updateTransportationDataAsDraft(
          commodityId,
          data,
          {
            saveDraft: true,
          }
        );
        if (success) handleNavigationDraft();
      },

      stage3: async () => {
        const success = await updateFuelAndEnergyDataAsDraft(commodityId, true);
        if (success) handleNavigationDraft();
      },

      stage4: async (data: FieldDetails) => {
        if (!commodityId) {
          return;
        }
        data?.commodity?.landUseChanges?.forEach(landUse => {
          const metadata = landUseMetaData.find(
            meta => meta.label === landUse.label
          );
          if (metadata) {
            landUse.from = metadata.from;
            landUse.to = metadata.to;
          }
        });
        data?.commodity?.landManagementChanges?.forEach(landManagement => {
          const metadata = landManagementMetaData.find(
            meta => meta.label === landManagement.label
          );
          if (metadata) {
            landManagement.from = metadata.from;
            landManagement.to = metadata.to;
          }
        });

        const success = await updateFieldDataAsDraft(commodityId, data, {
          saveDraft: true,
        });
        if (success) handleNavigationDraft();
      },

      stage5: async (data: FarmBoxCommodityDestribution) => {
        const success = await updateCommodityDistribution(
          Number(commodityId),
          data,
          true
        );
        if (success) handleNavigationDraft();
      },
    },
    draftButtonLoading: {
      rawMaterialDraftLoading,
      commodityDistributionDraftLoading,
      transportationDraftLoading,
      fuelAndEnergyDraftLoading,
      fieldDraftLoading,
    },
  };
};
